import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../global-components/src';
import { api } from '../../../services/api';
import { fallbackLinkPreview, LinkPreviewData } from '../../../utils/social-links';
import { SocialLinkPreview } from './social-link-preview';

interface Props {
  url: string;
  className?: string;
}

/**
 * Preview used before a comment or post is saved. Feed posts use the preview
 * persisted by the API instead, so scrolling never triggers third-party fetches.
 */
export const LinkPreview = ({ url, className }: Props) => {
  const { token } = useAuth();
  const fallback = useMemo(() => fallbackLinkPreview(url), [url]);
  const [preview, setPreview] = useState<LinkPreviewData | null>(fallback);

  useEffect(() => {
    setPreview(fallback);
    if (!fallback || !token) return;

    let active = true;
    const timeout = window.setTimeout(async () => {
      try {
        const { data } = await api(token).post('/posts/link-preview', { url: fallback.url });
        if (active) setPreview(data?.preview ?? fallback);
      } catch {
        // Keep the local card when metadata cannot be loaded.
      }
    }, 400);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [fallback, token]);

  return preview ? <SocialLinkPreview preview={preview} className={className} /> : null;
};
